import { useMemo, useState } from "react"

import { cn } from "@/lib/utils"
import {
  IconCheck,
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
} from "@tabler/icons-react"
import * as pagination from "@zag-js/pagination"
import { normalizeProps, useMachine } from "@zag-js/react"

import { Button } from "./Button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "./Command"
import { Popover, PopoverContent, PopoverTrigger } from "./Popover"

interface PaginationState {
  page: number
  pageSize: number
}

export interface PaginationProps {
  count: number
  state?: PaginationState
  onStateChange?: (state: PaginationState) => void
  pageSizeOptions?: number[]
  className?: string
}

export function Pagination({
  count,
  state: paginationState,
  onStateChange,
  pageSizeOptions = [10, 25, 50, 100],
  className,
}: PaginationProps) {
  const id = useMemo(() => Math.random().toString(36).substring(2, 9), [])
  const [open, setOpen] = useState(false)

  const service = useMachine(pagination.machine, {
    id,
    count,
    onPageChange: onStateChange,

    ...(paginationState
      ? {
          count,
          pageSize: paginationState.pageSize,
          page: paginationState.page,
        }
      : {}),
  })

  const api = pagination.connect(service, normalizeProps)
  if (api.totalPages === 0) return null

  const pageOptions = pageSizeOptions.map((pageSize) => ({
    value: pageSize,
    label: pageSize.toString(),
  }))

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <div className="inline-flex items-center gap-2 text-colors-text-secondary">
        <span>Show</span>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="neutral"
              size="small"
              role="combobox"
              aria-expanded={open}
              className="w-[70px] justify-between px-3 whitespace-nowrap"
              icon={IconChevronDown}
              iconPosition="right"
            >
              {pageOptions.find((option) => option.value === api.pageSize)?.label}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="z-[200] w-[80px] p-0">
            <Command>
              <CommandList>
                <CommandEmpty>No items found.</CommandEmpty>
                <CommandGroup>
                  {pageOptions.map((option) => (
                    <CommandItem
                      key={option.value}
                      value={option.value.toString()}
                      onSelect={(currentValue) => {
                        const pageSize = parseInt(currentValue)
                        onStateChange?.({
                          page: api.page,
                          pageSize,
                        })
                        api.setPageSize(pageSize)
                        setOpen(false)
                      }}
                    >
                      <IconCheck
                        className={cn(
                          "mr-2 h-4 w-4",
                          api.pageSize === option.value ? "opacity-100" : "opacity-0",
                        )}
                      />
                      {option.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <span className="text-body1 whitespace-nowrap">
          {api.pageRange.start + 1}-{Math.min(api.pageRange.end, count)} of {count}
        </span>
      </div>
      <nav {...api.getRootProps()}>
        <ul className="flex items-center gap-1">
          <li>
            <Button
              iconOnly
              icon={IconChevronLeft}
              variant="neutral"
              noBorder
              aria-label="previous page"
              {...api.getPrevTriggerProps()}
            />
          </li>
          {api.pages.map((page, i) => {
            if (page.type === "page") {
              return (
                <li key={page.value}>
                  <Button
                    variant="neutral"
                    noBorder={api.page !== page.value}
                    aria-label="previous page"
                    {...api.getItemProps(page)}
                    className="h-8 w-8 p-2"
                  >
                    {page.value}
                  </Button>
                </li>
              )
            } else
              return (
                <li
                  key={`ellipsis-${i}`}
                  className="flex h-8 w-8 items-center justify-center"
                >
                  <span className="p-2 px-2" {...api.getEllipsisProps({ index: i })}>
                    &#8230;
                  </span>
                </li>
              )
          })}
          <li>
            <Button
              iconOnly
              icon={IconChevronRight}
              variant="neutral"
              noBorder
              aria-label="next page"
              {...api.getNextTriggerProps()}
            />
          </li>
        </ul>
      </nav>
    </div>
  )
}
